/**
 * The preview's sound levels (1.7.4, docs/dev/editor-1.7.4-build.md "Audio levels"): a clip's gain at a source time
 * from its volume keys (audio-levels.ts volumeAt) plus its track's gain, and the crossfades at cuts. The sequence
 * viewer writes these to each element's GainNode on every frame it draws while playing (and at every seek), so the
 * schedule follows the keys and is re-planned whenever the position jumps. Pure.
 *
 * Crossfades: on an audio track both clips follow the equal-power curve over the blend [from, to) as the export does:
 * the incoming clip's element starts early from its head handle for the part before the cut, and (1.8.0) the outgoing
 * clip's element plays on past its Out from its tail handle for the part after it (prerollingAt). On A1 the picture
 * and its sound share one element, so the preview plays an A1 crossfade as two fades: the outgoing clip fades out
 * before the cut (over the part before it, or the whole length when it starts at the cut) and the incoming clip fades
 * in after it (over the part after it, or the whole length when it ends at the cut); the export is exact.
 */
import {
  allAudioTracks, clipDuration, crossfadeSpan, dbToGain, equalPower, layoutVideo, trackGainDb, volumeAt,
  type CrossfadeAlign, type SequenceDocument, type SoundTrack, type SourceDurations, type VolumeKey,
} from '@prism/contracts';
import type { SequenceSource } from '../../api/hooks';

/** A clip's level as a gain factor at a source time: its keys over gainDb, plus the track's gain (0 at −60 dB or below). */
export function levelGain(doc: SequenceDocument, clip: { gainDb: number; volumeKeys?: readonly VolumeKey[] }, track: SoundTrack, sourceTicks: number): number {
  return dbToGain(volumeAt(clip, sourceTicks) + trackGainDb(doc, track));
}

/**
 * The sources' lengths for the ops and the crossfade limits (1.8.0, SourceDurations by version id): what is recorded
 * so far for a recording, the length otherwise, Infinity for a still; a source of unknown length is left out.
 */
export function sourceLengths(items: Iterable<SequenceSource>): SourceDurations {
  const out = new Map<string, number>();
  for (const s of items) {
    if (s.mediaKind === 'image') out.set(s.versionId, Infinity);
    else if (s.live) out.set(s.versionId, s.live.recordedTicks);
    else if (s.durationTicks) out.set(s.versionId, s.durationTicks);
  }
  return out;
}

/** Whether a source is known to be silent (1.8.0, /sources hasAudio false): no level line, keys or crossfade. Not known: not silent. */
export const isSilent = (sources: ReadonlyMap<string, SequenceSource> | undefined, versionId: string): boolean => sources?.get(versionId)?.hasAudio === false;

/** One crossfade as the preview plays it. */
export interface PreviewCrossfade {
  incomingId: string;
  outgoingId: string;
  track: SoundTrack;
  cutTicks: number;
  ticks: number;
  /** 1.8.0: the parts before and after the cut (crossfadeSpan). */
  pre: number;
  post: number;
}

/** Every crossfade that applies (appliedCrossfade > 0), by the incoming clip's id and by the outgoing clip's id. */
export interface CrossfadePlan { byIncoming: Map<string, PreviewCrossfade>; byOutgoing: Map<string, PreviewCrossfade> }

export function crossfadePlan(doc: SequenceDocument, lengths?: SourceDurations): CrossfadePlan {
  const byIncoming = new Map<string, PreviewCrossfade>();
  const byOutgoing = new Map<string, PreviewCrossfade>();
  const add = (ref: { kind: 'video' | 'audio'; id: string }) => {
    const s = crossfadeSpan(doc, ref, lengths);
    if (!s) return;
    const x: PreviewCrossfade = { incomingId: s.cut.incoming.id, outgoingId: s.cut.outgoing.id, track: s.cut.track, cutTicks: s.cut.cutTicks, ticks: s.ticks, pre: s.pre, post: s.post };
    byIncoming.set(x.incomingId, x);
    byOutgoing.set(x.outgoingId, x);
  };
  for (const c of doc.video) if (c.crossfadeInTicks) add({ kind: 'video', id: c.id });
  for (const t of allAudioTracks(doc)) for (const c of t.clips) if (c.crossfadeInTicks) add({ kind: 'audio', id: c.id });
  return { byIncoming, byOutgoing };
}

export const NO_CROSSFADES: CrossfadePlan = { byIncoming: new Map(), byOutgoing: new Map() };

/**
 * The crossfade factor of a clip at record time `ph` (1 when no crossfade touches it there).
 *  - on an audio track: the outgoing clip equalPower(1 − p), the incoming one equalPower(p), p through [cut − pre, cut + post);
 *  - on A1 (the preview's approximation): the outgoing clip fades out over [cut − (pre or X), cut), the incoming one
 *    in over [cut, cut + (post or X)).
 */
export function crossfadeFactor(plan: CrossfadePlan, clipId: string, ph: number): number {
  let f = 1;
  const p = (x: PreviewCrossfade) => (ph - (x.cutTicks - x.pre)) / x.ticks;
  const out = plan.byOutgoing.get(clipId);
  if (out) {
    if (out.track !== 'a1') { if (ph >= out.cutTicks - out.pre && ph < out.cutTicks + out.post) f *= equalPower(1 - p(out)); } else {
      const len = out.pre || out.ticks;
      if (ph >= out.cutTicks - len && ph < out.cutTicks) f *= equalPower(1 - (ph - (out.cutTicks - len)) / len);
    }
  }
  const inc = plan.byIncoming.get(clipId);
  if (inc) {
    if (inc.track !== 'a1') { if (ph >= inc.cutTicks - inc.pre && ph < inc.cutTicks + inc.post) f *= equalPower(p(inc)); } else {
      const len = inc.post || inc.ticks;
      if (ph >= inc.cutTicks && ph < inc.cutTicks + len) f *= equalPower((ph - inc.cutTicks) / len);
    }
  }
  return f;
}

/**
 * The audio-track clips heard outside their own range because of a crossfade (on an audio track): an incoming clip
 * pre-rolled before its start (from its head handle) and (1.8.0) an outgoing clip played on past its end (from its
 * tail handle), each with the source time it plays at `ph` and the other clip of the cut (`partnerId`).
 */
export function prerollingAt(doc: SequenceDocument, plan: CrossfadePlan, ph: number): Array<{ clipId: string; track: string; sourceTicks: number; partnerId: string }> {
  const out: Array<{ clipId: string; track: string; sourceTicks: number; partnerId: string }> = [];
  const clipOn = (track: string, id: string) => allAudioTracks(doc).find((t) => t.id === track)?.clips.find((k) => k.id === id);
  for (const x of plan.byIncoming.values()) {
    if (x.track === 'a1') continue;
    if (ph >= x.cutTicks - x.pre && ph < x.cutTicks) {
      const c = clipOn(x.track, x.incomingId);
      if (c) out.push({ clipId: c.id, track: x.track, sourceTicks: c.sourceInTicks - (x.cutTicks - ph), partnerId: x.outgoingId });
    } else if (ph >= x.cutTicks && ph < x.cutTicks + x.post) {
      const c = clipOn(x.track, x.outgoingId);
      if (c) out.push({ clipId: c.id, track: x.track, sourceTicks: c.sourceOutTicks + (ph - x.cutTicks), partnerId: x.incomingId });
    }
  }
  return out;
}

/** A crossfade as the timeline draws it: the incoming clip, its applied length, the cut and (1.8.0) the blend [fromTicks, toTicks) and its alignment. */
export interface CrossfadeMark { id: string; kind: 'video' | 'audio'; cutTicks: number; ticks: number; fromTicks: number; toTicks: number; align: CrossfadeAlign }

/** Where crossfades are drawn on the timeline: each incoming V1 or audio clip with its crossfade as it applies. */
export function crossfadeMarks(doc: SequenceDocument, lengths?: SourceDurations): CrossfadeMark[] {
  const out: CrossfadeMark[] = [];
  const add = (kind: 'video' | 'audio', id: string) => {
    const s = crossfadeSpan(doc, { kind, id }, lengths);
    if (s) out.push({ id, kind, cutTicks: s.cut.cutTicks, ticks: s.ticks, fromTicks: s.fromTicks, toTicks: s.toTicks, align: s.align });
  };
  for (const p of layoutVideo(doc)) if (p.clip.crossfadeInTicks) add('video', p.clip.id);
  for (const t of allAudioTracks(doc)) for (const c of t.clips) if (c.crossfadeInTicks && clipDuration(c) > 0) add('audio', c.id);
  return out;
}
